import { flightLegRowToLeg, flightLegToRow, type FlightLegRow } from "../lib/flightDbMap";
import type { FlightLeg } from "../lib/types";
import { getSupabase } from "./client";

export async function listFlightLegs(): Promise<{
  data: FlightLeg[] | null;
  error: string | null;
}> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const { data, error } = await sb
    .from("flight_legs")
    .select("*")
    .order("flight_date", { ascending: false });
  if (error) return { data: null, error: error.message };
  const rows = (data ?? []) as FlightLegRow[];
  return { data: rows.map(flightLegRowToLeg), error: null };
}

export async function getFlightLeg(
  id: string
): Promise<{ data: FlightLeg | null; error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { data: null, error: "Supabase is not configured." };
  const { data, error } = await sb.from("flight_legs").select("*").eq("id", id).maybeSingle();
  if (error) return { data: null, error: error.message };
  if (!data) return { data: null, error: "Not found." };
  return { data: flightLegRowToLeg(data as FlightLegRow), error: null };
}

export async function insertFlightLegs(
  legs: FlightLeg[]
): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: "Supabase is not configured." };
  const rows = legs.map((l) => {
    const r = flightLegToRow({ ...l, id: undefined });
    delete r.id;
    return r;
  });
  const { error } = await sb.from("flight_legs").insert(rows);
  if (error) return { error: error.message };
  return { error: null };
}

export async function upsertManualLegs(legs: FlightLeg[]): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: "Supabase is not configured." };
  for (const leg of legs) {
    const base = flightLegToRow(leg);
    if (leg.id) {
      const { id, ...rest } = base;
      const { error } = await sb.from("flight_legs").update(rest).eq("id", leg.id);
      if (error) return { error: error.message };
    } else {
      const { id: _id, ...rest } = base;
      const { error } = await sb.from("flight_legs").insert(rest);
      if (error) return { error: error.message };
    }
  }
  return { error: null };
}

export async function updateFlightLeg(
  id: string,
  leg: FlightLeg
): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: "Supabase is not configured." };
  const row = flightLegToRow({ ...leg, id });
  const { id: _id, ...rest } = row;
  const { error } = await sb.from("flight_legs").update(rest).eq("id", id);
  if (error) return { error: error.message };
  return { error: null };
}

export async function deleteFlightLeg(id: string): Promise<{ error: string | null }> {
  const sb = getSupabase();
  if (!sb) return { error: "Supabase is not configured." };
  const { error } = await sb.from("flight_legs").delete().eq("id", id);
  if (error) return { error: error.message };
  return { error: null };
}
